import { Bell, Loader2, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Section, Row, Toggle } from "./ProfileFieldPrimitives";
import usePushNotifications from "@/hooks/usePushNotifications";

/**
 * NotificationsSection — surfaces browser/device push controls inside the
 * profile editor. Handles the three states a user can be in:
 *   • unsupported (older browser or iOS Safari without PWA install)
 *   • permission=denied (has to unblock in browser settings)
 *   • supported + granted → enable/disable + per-category prefs + test button
 */
export default function NotificationsSection() {
  const { supported, permission, subscribed, busy, prefs, setPrefs, enable, disable, sendTest } = usePushNotifications();

  const handleEnable = async () => {
    const res = await enable();
    if (res?.ok) toast.success("Notifications on — sending you a quick test.");
    else if (res?.reason === "permission") toast.error("Permission denied. Unblock notifications in your browser settings.");
    else toast.error(res?.reason || "Could not enable notifications.");
    if (res?.ok) sendTest();
  };

  const handleDisable = async () => {
    await disable();
    toast.success("Notifications turned off on this device.");
  };

  const handleTest = async () => {
    const res = await sendTest();
    if (res.ok) toast.success(`Sent to ${res.delivered} device${res.delivered === 1 ? "" : "s"}.`);
    else toast.error("Test failed.");
  };

  return (
    <Section icon={Bell} title="Notifications">
      <div className="p-4 space-y-3" data-testid="notifications-section">
        {!supported && (
          <UnsupportedBanner />
        )}
        {supported && permission === "denied" && (
          <div className="flex items-start gap-2 text-sm text-rose-300 border border-rose-500/30 bg-rose-950/20 rounded-md px-3 py-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            Notifications are blocked in your browser. Open site settings → Permissions → allow Notifications, then reload.
          </div>
        )}
        {supported && permission !== "denied" && !subscribed && (
          <>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Get pinged on this device for new messages, likes, gifts, and when someone you
              follow goes live. Works on Android + desktop; iOS needs the site added to your
              home screen (iOS 16.4+).
            </p>
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy}
              data-testid="notifications-enable"
              className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-2"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Enabling…</> : <><Bell size={14} /> Enable notifications on this device</>}
            </button>
          </>
        )}

        {supported && subscribed && (
          <>
            <div className="text-xs uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
              <Bell size={12} /> Notifications are ON for this device
            </div>
            <div className="border border-white/10 rounded-md divide-y divide-white/5 bg-black/40">
              <PrefRow label="New messages" testId="notifications-pref-message" value={prefs.message} onChange={(v) => setPrefs({ ...prefs, message: v })} />
              <PrefRow label="Likes on your posts" testId="notifications-pref-like" value={prefs.like} onChange={(v) => setPrefs({ ...prefs, like: v })} />
              <PrefRow label="Gifts sent to you" testId="notifications-pref-gift" value={prefs.gift} onChange={(v) => setPrefs({ ...prefs, gift: v })} />
              <PrefRow label="Someone you follow goes live" testId="notifications-pref-live" value={prefs.live_start} onChange={(v) => setPrefs({ ...prefs, live_start: v })} />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleTest}
                data-testid="notifications-test"
                className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1"
              >
                <Send size={12} /> Send test
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={handleDisable}
                disabled={busy}
                data-testid="notifications-disable"
                className="text-xs text-zinc-500 hover:text-white"
              >
                Turn off for this device
              </button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

function PrefRow({ label, testId, value, onChange }) {
  return (
    <Row
      label={label}
      testId={testId}
      right={<Toggle checked={value} onChange={onChange} testId={`${testId}-toggle`} />}
    />
  );
}

function UnsupportedBanner() {
  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  return (
    <div className="flex items-start gap-2 text-sm text-amber-300 border border-amber-500/30 bg-amber-950/20 rounded-md px-3 py-2">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <span>
        {isIOS
          ? "iOS Safari only supports notifications if you tap Share → Add to Home Screen, then open Panda X from that icon."
          : "Your browser doesn't support push notifications. Try Chrome, Firefox, or Edge on desktop or Android."}
      </span>
    </div>
  );
}
